import Phaser from 'phaser';
import { getTopScores } from '@/storage/score-repository';
import { SCORING } from '@/config/scoring';
import { NEON_FONTS } from '@/config/colors';

const FONT_TITLE = NEON_FONTS.TITLE;
const FONT_BODY = NEON_FONTS.BODY;
const HEADER_FONT_SIZE = '24px';
const COLUMN_HEADER_FONT_SIZE = '9px';
const ENTRY_FONT_SIZE = '9px';
const BACK_FONT_SIZE = '12px';
const EMPTY_FONT_SIZE = '14px';

const TITLE_Y = 22;
const COLUMN_HEADER_Y = 52;
const ENTRIES_START_Y = 70;
const ENTRY_LINE_HEIGHT = 22;

interface SceneData {
  readonly highlightId?: string;
}

export class LeaderboardScene extends Phaser.Scene {
  private highlightId: string | null = null;
  private isTransitioning = false;

  constructor() {
    super({ key: 'LeaderboardScene' });
  }

  create(data?: SceneData): void {
    this.highlightId = data?.highlightId ?? null;
    this.isTransitioning = false;

    const { width, height } = this.cameras.main;
    const centerX = width / 2;

    this.add.text(centerX, TITLE_Y, 'LEADERBOARD', {
      fontFamily: FONT_TITLE,
      fontSize: HEADER_FONT_SIZE,
      fontStyle: 'bold',
      color: '#FFE500',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 12, fill: true },
    }).setOrigin(0.5);

    this.renderColumnHeaders(centerX);

    const scores = getTopScores(SCORING.MAX_LEADERBOARD_ENTRIES);

    if (scores.length === 0) {
      this.renderEmptyState(centerX, height);
    } else {
      this.renderScoreEntries(scores, centerX);
    }

    this.renderBackPrompt(centerX, height);
    this.setupInputHandlers();
  }

  private renderColumnHeaders(centerX: number): void {
    const headerText = '#   NAME   SCORE      LVL   DATE';
    this.add.text(centerX, COLUMN_HEADER_Y, headerText, {
      fontFamily: FONT_BODY,
      fontSize: COLUMN_HEADER_FONT_SIZE,
      fontStyle: 'bold',
      color: '#00E5FF',
      align: 'center',
    }).setOrigin(0.5);

    const lineY = COLUMN_HEADER_Y + 10;
    const lineWidth = 380;
    const line = this.add.graphics();
    line.lineStyle(1, 0x444444);
    line.lineBetween(centerX - lineWidth / 2, lineY, centerX + lineWidth / 2, lineY);
  }

  private renderEmptyState(centerX: number, height: number): void {
    this.add.text(centerX, height / 2, 'No scores yet!\nPlay a game to get started.', {
      fontFamily: FONT_BODY,
      fontSize: EMPTY_FONT_SIZE,
      color: '#AABBCC',
      align: 'center',
      lineSpacing: 10,
    }).setOrigin(0.5);
  }

  private renderScoreEntries(
    scores: ReadonlyArray<{ readonly id: string; readonly initials: string; readonly score: number; readonly levelReached: number; readonly date: string }>,
    centerX: number,
  ): void {
    scores.forEach((entry, index) => {
      const rank = String(index + 1).padStart(2, ' ');
      const initials = entry.initials.padEnd(3, ' ');
      const score = String(entry.score).padStart(8, ' ');
      const level = String(entry.levelReached).padStart(3, ' ');
      const dateStr = formatDate(entry.date);

      const lineText = `${rank}.  ${initials}  ${score}  ${level}  ${dateStr}`;

      const isHighlighted = this.highlightId !== null && entry.id === this.highlightId;
      const color = isHighlighted ? '#FFE500' : '#DDEEFF';

      const entryText = this.add.text(
        centerX,
        ENTRIES_START_Y + index * ENTRY_LINE_HEIGHT,
        lineText,
        {
          fontFamily: FONT_BODY,
          fontSize: ENTRY_FONT_SIZE,
          fontStyle: 'bold',
          color,
          align: 'center',
          shadow: isHighlighted
            ? { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 8, fill: true }
            : undefined,
        },
      ).setOrigin(0.5);

      if (isHighlighted) {
        this.tweens.add({
          targets: entryText,
          alpha: { from: 1, to: 0.4 },
          duration: 500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    });
  }

  private renderBackPrompt(centerX: number, height: number): void {
    const backText = this.add.text(centerX, height - 25, 'PRESS ANY KEY TO RETURN', {
      fontFamily: FONT_BODY,
      fontSize: BACK_FONT_SIZE,
      fontStyle: 'bold',
      color: '#00FF88',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 8, fill: true },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    backText.on('pointerdown', () => {
      this.returnToMenu();
    });
  }

  private setupInputHandlers(): void {
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown', () => {
        this.returnToMenu();
      });
    }
  }

  private returnToMenu(): void {
    if (this.isTransitioning) {
      return;
    }
    this.isTransitioning = true;
    this.scene.start('MenuScene');
  }
}

function formatDate(dateStr: string): string {
  if (dateStr.length >= 10) {
    return dateStr.substring(0, 10);
  }
  return dateStr;
}
